import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CurrencySelect } from '@/components/ui/currency-select';
import { useCreateCashInflow, INFLOW_CATEGORY_LABELS, InflowCategory } from '@/hooks/finance/useCashInflows';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { format } from 'date-fns';
import { BusinessUnitSelect } from '@/components/finance/BusinessUnitSelect';
import { DEFAULT_BUSINESS_UNIT, BusinessUnit } from '@/constants/businessUnits';

const NewCashInflowPage = () => {
  const navigate = useNavigate();
  const createInflow = useCreateCashInflow();
  const { data: bankAccounts } = useBankAccounts(true);

  const [formData, setFormData] = useState({
    inflow_date: format(new Date(), 'yyyy-MM-dd'),
    category: 'capital_injection' as InflowCategory,
    amount: '',
    currency: 'ETB',
    bank_account_id: '',
    description: '',
    reference_number: '',
    source_name: '',
    business_unit: DEFAULT_BUSINESS_UNIT as BusinessUnit,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.amount || parseFloat(formData.amount) <= 0) return;

    createInflow.mutate({
      inflow_date: formData.inflow_date,
      category: formData.category,
      amount: parseFloat(formData.amount),
      currency: formData.currency,
      bank_account_id: formData.bank_account_id || null,
      description: formData.description || undefined,
      reference_number: formData.reference_number || undefined,
      source_name: formData.source_name || undefined,
      business_unit: formData.business_unit,
    } as any, {
      onSuccess: () => navigate('/finance/accounting/cash-inflows'),
    });
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/finance/accounting/cash-inflows')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">New Cash Inflow</h1>
            <p className="text-muted-foreground">Record a capital injection, loan, or other financing inflow</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader><CardTitle>Inflow Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date <span className="text-destructive">*</span></Label>
                  <Input
                    type="date"
                    value={formData.inflow_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, inflow_date: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category <span className="text-destructive">*</span></Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v as InflowCategory }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(Object.entries(INFLOW_CATEGORY_LABELS) as [InflowCategory, string][]).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount <span className="text-destructive">*</span></Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <CurrencySelect
                    value={formData.currency}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, currency: v }))}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Unit *</Label>
                  <BusinessUnitSelect
                    value={formData.business_unit}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, business_unit: v }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bank Account</Label>
                  <Select value={formData.bank_account_id} onValueChange={(v) => setFormData(prev => ({ ...prev, bank_account_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select bank account" /></SelectTrigger>
                    <SelectContent>
                      {bankAccounts?.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}{a.bank_name ? ` (${a.bank_name})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Source Name</Label>
                <Input
                  placeholder="e.g. Investor name, bank name"
                  value={formData.source_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, source_name: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Reference Number</Label>
                <Input
                  placeholder="e.g. Transfer reference"
                  value={formData.reference_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Notes about this inflow..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/finance/accounting/cash-inflows')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createInflow.isPending || !formData.amount}>
                  {createInflow.isPending ? 'Recording...' : 'Record Inflow'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </Layout>
  );
};

export default NewCashInflowPage;
